import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';

import { cn } from '@/shadcn-ui/utils';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface RadioGroupProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root> {}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(
  (
    {
      className,
      ...props
    }: React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>,
    ref
  ) => {
    return (
      <RadioGroupPrimitive.Root
        className={cn('grid gap-2', className)}
        {...props}
        ref={ref}
      />
    );
  }
);
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  selected?: boolean;
}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, selected = false, ...props }: RadioGroupItemProps, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'flex aspect-square h-5 w-5 items-center justify-center rounded-full text-flexcar-blue30 text-primary ring-[1.5px] ring-inset ring-black ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        {
          'fill-flexcar-blue30 text-flexcar-blue30 ring-flexcar-blue30': selected,
        },
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

interface RadioItemLabelProps
  extends React.DetailedHTMLProps<
    React.LabelHTMLAttributes<HTMLLabelElement>,
    HTMLLabelElement
  > {
  selected: boolean;
  disabled?: boolean;
}

const RadioItemLabel = React.forwardRef<HTMLLabelElement, RadioItemLabelProps>(
  (
    { children, selected, className, disabled = false, ...props }: RadioItemLabelProps,
    ref
  ) => {
    return (
      <label
        ref={ref}
        {...props}
        className={cn(
          'flex w-full cursor-pointer flex-col justify-center gap-2 rounded-[16px] p-4',
          {
            'shadow-boxSelected ring-2 ring-flexcar-blue30': selected,
            'shadow-none ring-1.5 ring-flexcar-neutral80': !selected || disabled,
            'cursor-default': disabled,
          },
          className
        )}
      >
        {children}
      </label>
    );
  }
);
RadioItemLabel.displayName = 'RadioItemLabel';

export { RadioGroup, RadioGroupItem, RadioItemLabel };
